/**
 * Pure helpers for sea-route computation.
 *
 * No React / React Native / Expo imports, so unit tests AND build tooling can
 * use these directly. `computeSeaRoutes` is the single implementation shared by
 * the game renderer (MapView) and the map editor.
 */
import {
  parsePolygonVertices,
  pointInPolygon,
  polygonsShareEdge,
  closestVertexPair,
  closestEdgeMidpointPair,
  centersCrossingSegment,
  lineCrossesPolygon,
  forcedCrossingSegment,
  applySeaRouteTuning,
  type SeaRouteTuning,
} from './badgeHelpers'

/**
 * Build a canonical set of "a|b" keys (sorted so a ≤ b lexicographically)
 * from a list of territory-id pairs.
 *
 * Used by computeSeaRoutes in MapView.tsx to build:
 *   - the wraparound set  (pairs that get edge-stub rendering instead)
 *   - the suppress set    (land borders the geometric classifier missed)
 *
 * Canonical order means the test never has to care which direction the
 * pair is listed: ['ca','nv'] and ['nv','ca'] both produce 'ca|nv'.
 */
export function buildCanonicalPairSet(
  pairs: ReadonlyArray<[string, string]> | undefined,
): Set<string> {
  const s = new Set<string>()
  for (const [a, b] of pairs ?? []) {
    s.add(a < b ? `${a}|${b}` : `${b}|${a}`)
  }
  return s
}

/**
 * Build the cache key for the module-level `_seaRouteCache` in MapView.tsx.
 *
 * Sea-route geometry depends on `mapX` and `mapW` because the wraparound-stub
 * pass anchors its endpoints at the exact left/right edges of the viewBox
 * (`mapX` and `mapX + mapW`). In landscape mode these values are PADDED by
 * `computeMapDimensions` based on the screen dimensions and reserved-left/right
 * amounts — they are NOT static per map. A key that omits them would cache the
 * first-render stale result (possibly computed at a different reserved-left
 * context) and return it for subsequent renders with different dimensions.
 *
 * `mapX` and `mapW` are rounded to integers so sub-pixel oscillations don't
 * produce spurious cache misses.
 *
 * The `suppressKey` (JSON of the suppress list) is included so results don't
 * bleed across geometry revisions that differ in their suppress set.
 */
export function buildSeaRouteCacheKey(
  geometryVersion: string,
  territoryCount: number,
  suppressKey: string,
  mapX: number,
  mapW: number,
): string {
  return `${geometryVersion}-${territoryCount}-${suppressKey}-${Math.round(mapX)}-${Math.round(mapW)}`
}

/**
 * Whether routes with wraparound stubs can use the map's base-viewBox
 * geometry. Landscape pads the viewBox, so its stubs must be recomputed
 * against that viewport; portrait keeps the native map dimensions.
 */
export function canUseBaseViewBoxSeaRoutes(
  mapX: number,
  mapW: number,
  viewBox: { x?: number; width: number },
): boolean {
  return (
    Math.round(mapX) === Math.round(viewBox.x ?? 0) &&
    Math.round(mapW) === Math.round(viewBox.width)
  )
}

// ─── Sea-route computation ────────────────────────────────────────────────────
//
// Lives here rather than in MapView so it has NO React / react-native imports
// and can therefore be reused by tooling. `tools/map-editor/extract-maps.ts`
// bakes the result into the editor's map JSON so the map editor draws exactly
// the crossings the game draws — a map feature the editor can't show is a
// feature the maintainer can't review (see AGENTS.md → Quality bar).

export interface SeaRoute {
  aId: string
  bId: string
  from: { x: number; y: number }
  to: { x: number; y: number }
  /** A one-way crossing (Territory.oneWayBorders): `aId` attacks `bId` only. Draws an arrowhead at `to`. */
  oneWay?: boolean
}

/** Minimal territory shape this needs — keeps tooling free of GameState. */
export interface SeaRouteTerritory {
  id: string
  shape?: string | undefined
  adjacencies: ReadonlyArray<string>
  oneWayBorders?: ReadonlyArray<string> | undefined
}

type Pairs = ReadonlyArray<[string, string]> | undefined
/** Per-pair cosmetic tuning for forced crossings, keyed "a|b" (ids sorted). */
export type ForceSeaRouteTuning = Record<string, SeaRouteTuning> | undefined

export function computeSeaRoutes(
  territories: ReadonlyArray<SeaRouteTerritory>,
  wraparoundAdjacencies: Pairs,
  suppressSeaRoutes: Pairs,
  forceSeaRoutes: Pairs,
  mapX: number,
  mapW: number,
  forceSeaRouteTuning?: ForceSeaRouteTuning,
): SeaRoute[] {
  const verticesById = new Map<string, Array<[number, number]>>()
  for (const t of territories) {
    verticesById.set(t.id, parsePolygonVertices(t.shape))
  }

  // Wraparound pairs are rendered as two edge-stubs instead of a
  // straight cross-map line. Build a Set of "a|b" keys (canonical
  // sorted order) so the loop below can skip the normal sea-route
  // emission for these, then we add the stubs separately.
  const wraparoundSet = buildCanonicalPairSet(wraparoundAdjacencies)

  // Pairs listed in suppressSeaRoutes are genuinely land-adjacent but
  // their SVG vertices don't coincide, so polygonsShareEdge returns false
  // and would otherwise emit a spurious dashed connector. Skip them here.
  const suppressSet = buildCanonicalPairSet(suppressSeaRoutes)

  // Pairs in forceSeaRoutes are water crossings whose polygons TOUCH, so
  // polygonsShareEdge calls them land and no connector would be drawn. These
  // are the straits — the most important borders on a map like Great Lakes.
  const forceSet = buildCanonicalPairSet(forceSeaRoutes)

  // The crossing between two territories that don't share a border, or null
  // when they touch (unless forced) or the line would cross a third territory.
  const crossing = (fromId: string, toId: string, key: string): { from: [number, number]; to: [number, number] } | null => {
    const va = verticesById.get(fromId) ?? []
    const vb = verticesById.get(toId) ?? []
    if (va.length === 0 || vb.length === 0) return null
    const forced = forceSet.has(key)
    if (!forced && polygonsShareEdge(va, vb)) return null

    // A forced pair whose polygons touch has a zero-length closest-vertex
    // pair, so draw a gate straddling the contact instead. When a forced pair
    // does NOT touch there is nothing special to do: it takes the ordinary
    // path and looks like any other sea route, which is what makes listing
    // all of a map's straits harmless.
    const gate = forced ? forcedCrossingSegment(va, vb) : null
    const tuning = forceSeaRouteTuning?.[key]
    const midpointPair = forced && tuning?.endpointMode === 'edge-midpoints'
      ? closestEdgeMidpointPair(va, vb)
      : null
    // 'centers' applies to any route, forced or not: the line between the
    // two territories' centres, clipped to their coasts.
    const centersPair = tuning?.endpointMode === 'centers' ? centersCrossingSegment(va, vb) : null
    // An explicit endpoint mode is a deliberate base-geometry choice. It
    // must win over the generic touching-polygon gate; otherwise the gate
    // remains vertex-to-vertex even though the map asked for edge middles.
    const base = centersPair ?? midpointPair ?? gate ?? closestVertexPair(va, vb)
    if (!base) return null
    // Cosmetic tuning applies to EVERY route, gate or not.
    const pair = applySeaRouteTuning(base, forceSeaRouteTuning?.[key])
    if (!pair) return null
    // Reject lines that pass through any third territory's polygon
    // — the dashed connector must cross only water, never land.
    // We sample interior points along the candidate line and bail
    // as soon as one lands inside another polygon.
    let crossesLand = false
    // FORCED pairs are exempt, gate or not. A gate straddles the border, so
    // both halves lie inside a polygon by construction and the veto would
    // always reject it — but the same reasoning covers a forced pair with a
    // real gap: the map author asserted the crossing exists, so a third
    // polygon in the way is a reason to AIM the dash (forceSeaRouteTuning),
    // not to silently drop it.
    //
    // world-classic is why. East Africa ↔ Madagascar is a genuine adjacency
    // whose closest-vertex line clips South Africa for 10 units, so the route
    // vanished with nothing to indicate the two were connected — and it could
    // not be fixed in the map editor either, because the editor draws the
    // routes baked by extract-maps.ts, which bakes them UNTUNED. A route that
    // only survives once tuned therefore never appeared there to be dragged.
    if (forced) crossesLand = false
    else for (const [otherId, otherVerts] of verticesById) {
      if (otherId === fromId || otherId === toId) continue
      if (otherVerts.length < 3) continue
      if (lineCrossesPolygon(pair.from, pair.to, otherVerts)) {
        crossesLand = true
        break
      }
    }
    if (crossesLand) return null

    return pair
  }

  const routes: SeaRoute[] = []
  const seen = new Set<string>()
  for (const t of territories) {
    for (const adjId of t.adjacencies) {
      const key = t.id < adjId ? `${t.id}|${adjId}` : `${adjId}|${t.id}`
      if (seen.has(key)) continue
      seen.add(key)
      if (wraparoundSet.has(key)) continue // handled below
      if (suppressSet.has(key)) continue // land border, geometric classifier missed it
      const pair = crossing(t.id, adjId, key)
      if (!pair) continue
      routes.push({
        aId: t.id,
        bId: adjId,
        from: { x: pair.from[0], y: pair.from[1] },
        to: { x: pair.to[0], y: pair.to[1] },
      })
    }
  }

  // One-way crossings (Territory.oneWayBorders): drawn from the attacker to the
  // target with an arrowhead, e.g. Royal Rumble's Landing -> Gate. One-way
  // borders between territories that touch (Capture the Flag) draw nothing.
  for (const t of territories) {
    for (const toId of t.oneWayBorders ?? []) {
      const key = t.id < toId ? `${t.id}|${toId}` : `${toId}|${t.id}`
      if (seen.has(key) || suppressSet.has(key)) continue
      seen.add(key)
      const pair = crossing(t.id, toId, key)
      if (!pair) continue
      routes.push({ aId: t.id, bId: toId, from: { x: pair.from[0], y: pair.from[1] }, to: { x: pair.to[0], y: pair.to[1] }, oneWay: true })
    }
  }

  // ── Wraparound stubs ────────────────────────────────────────────
  // For each wraparound pair, emit TWO short routes:
  //   1. From the EASTERN cell's rightmost vertex to the right edge
  //      of the map (mapX + mapW) at the same y.
  //   2. From the left edge (mapX) at the western cell's leftmost
  //      vertex's y, INTO that vertex.
  // The "eastern" cell is whichever has the larger min-x bounding-
  // box position; the western has the smaller. Vertical alignment
  // uses each cell's own y so the stubs sit at the right latitude
  // for that cell rather than averaging.
  for (const pair of wraparoundAdjacencies ?? []) {
    const [aId, bId] = pair
    const va = verticesById.get(aId) ?? []
    const vb = verticesById.get(bId) ?? []
    if (va.length === 0 || vb.length === 0) continue
    const xsA = va.map(([x]) => x)
    const xsB = vb.map(([x]) => x)
    const eastId = Math.max(...xsA) > Math.max(...xsB) ? aId : bId
    const westId = eastId === aId ? bId : aId
    const eastVerts = verticesById.get(eastId) ?? []
    const westVerts = verticesById.get(westId) ?? []
    if (eastVerts.length === 0 || westVerts.length === 0) continue
    // East cell: pick the rightmost vertex (max x).
    let eastVert: [number, number] = eastVerts[0]!
    for (const v of eastVerts) if (v[0] > eastVert[0]) eastVert = v
    // West cell: pick the leftmost vertex (min x).
    let westVert: [number, number] = westVerts[0]!
    for (const v of westVerts) if (v[0] < westVert[0]) westVert = v
    routes.push({
      aId: eastId,
      bId: `__edge_right_${westId}`,
      from: { x: eastVert[0], y: eastVert[1] },
      to: { x: mapX + mapW, y: eastVert[1] },
    })
    routes.push({
      aId: `__edge_left_${eastId}`,
      bId: westId,
      from: { x: mapX, y: westVert[1] },
      to: { x: westVert[0], y: westVert[1] },
    })
  }

  return routes
}

/** The canonical "a|b" key of a route, ids sorted, matching the map editor's. */
export function routePairKey(aId: string, bId: string): string {
  return aId < bId ? `${aId}|${bId}` : `${bId}|${aId}`
}

/** How a crossing's custom dice (Territory.borderDice) treat the attacker. */
export type DiceRouteKind = 'disadvantage' | 'advantage'

/**
 * Crossings that roll custom dice, keyed "a|b" (ids sorted), by what they do to
 * the attacker: worse dice (an attack below 6 or a defence above it, e.g. 5 v 6)
 * draw orange, better dice red. A crossing that is worse either way wins, so a
 * strait that hurts any attacker reads as hard.
 */
export function customDiceRouteKinds(
  territories: readonly { id: string; borderDice?: Record<string, { attack?: number; defend?: number } | number> }[],
): Map<string, DiceRouteKind> {
  const kinds = new Map<string, DiceRouteKind>()
  for (const t of territories) {
    for (const [other, dice] of Object.entries(t.borderDice ?? {})) {
      const attack = typeof dice === 'number' ? dice : dice.attack ?? 6
      const defend = typeof dice === 'number' ? 6 : dice.defend ?? 6
      const kind: DiceRouteKind | null = attack < 6 || defend > 6 ? 'disadvantage' : attack > 6 || defend < 6 ? 'advantage' : null
      if (!kind) continue
      const key = routePairKey(t.id, other)
      if (kinds.get(key) !== 'disadvantage') kinds.set(key, kind)
    }
  }
  return kinds
}

/**
 * Arrowhead for a one-way crossing: a triangle whose tip sits at `to`, pointing
 * along from -> to. Returns SVG polygon points. The map editor mirrors this in
 * tools/map-editor/app.js (oneWayArrowPoints); keep the two in step.
 */
export function oneWayArrowPoints(route: Pick<SeaRoute, 'from' | 'to'>, size: number): string {
  const dx = route.to.x - route.from.x, dy = route.to.y - route.from.y
  const len = Math.hypot(dx, dy) || 1
  const ux = dx / len, uy = dy / len
  const bx = route.to.x - ux * size, by = route.to.y - uy * size
  const half = size * 0.6
  const pts: Array<[number, number]> = [[route.to.x, route.to.y], [bx - uy * half, by + ux * half], [bx + uy * half, by - ux * half]]
  return pts.map(([x, y]) => `${Math.round(x * 100) / 100},${Math.round(y * 100) / 100}`).join(' ')
}

/** A territory outline as separate rings, one per subpath (islands, holes). */
export function outlineRings(d: string | undefined): Array<Array<[number, number]>> {
  if (!d) return []
  return d.split(/(?=M)/).map((sub) => parsePolygonVertices(sub)).filter((ring) => ring.length >= 3)
}

function insideRings(p: [number, number], rings: Array<Array<[number, number]>>): boolean {
  let inside = false
  for (const ring of rings) if (pointInPolygon(p, ring)) inside = !inside
  return inside
}

function distanceToRings(p: [number, number], rings: Array<Array<[number, number]>>): number {
  let best = Infinity
  for (const ring of rings) {
    for (let i = 0; i < ring.length; i++) {
      const [ax, ay] = ring[i]!, [bx, by] = ring[(i + 1) % ring.length]!
      const dx = bx - ax, dy = by - ay, len2 = dx * dx + dy * dy
      const t = len2 > 0 ? Math.max(0, Math.min(1, ((p[0] - ax) * dx + (p[1] - ay) * dy) / len2)) : 0
      best = Math.min(best, Math.hypot(p[0] - ax - t * dx, p[1] - ay - t * dy))
    }
  }
  return best
}

export interface RouteEndClearance {
  /** Radius of the route's round end cap: half its outline (halo) width. */
  cap: number
  /** How far each territory's rendered edge reaches past its polygon into the water. */
  edge?: number
  /** One-way routes: the arrowhead size passed to oneWayArrowPoints, and its outline width. */
  arrowSize?: number
  arrowStroke?: number
}

/**
 * Run a route's line ends right up to the coasts. Each end goes to where the
 * route's line first meets its own territory, walking out from the midpoint
 * (so a hand-tuned route that stops short is extended, and one that overruns
 * is pulled back), then one cap radius further into the land. The renderer
 * clips lines to the water (`clipToWater`), so the line is cut exactly at the
 * rendered coast at any angle and its outline never paints over a border.
 *
 * A one-way route's arrowhead is not clipped: its tip stops where the whole
 * head (tip and base corners, with their outline) still clears the coast.
 * Gates straddling a touching border (forced crossings) can't be clipped to
 * water and are returned as authored. An end aimed past its coast, or with no
 * territory (wraparound stubs), keeps its authored position. The map editor
 * mirrors this in tools/map-editor/app.js (clearRouteEnds); keep them in step.
 */
export function clearRouteEnds(
  route: SeaRoute,
  ringsById: ReadonlyMap<string, Array<Array<[number, number]>>>,
  { cap, edge = 0, arrowSize = 0, arrowStroke = 0 }: RouteEndClearance,
): SeaRoute & { clipToWater: boolean } {
  const { from, to } = route
  const dx = to.x - from.x, dy = to.y - from.y
  const len = Math.hypot(dx, dy)
  if (len === 0) return { ...route, clipToWater: false }
  const ux = dx / len, uy = dy / len
  const mid: [number, number] = [from.x + dx / 2, from.y + dy / 2]
  const ringsA = ringsById.get(route.aId), ringsB = ringsById.get(route.bId)
  if ((ringsA && insideRings(mid, ringsA)) || (ringsB && insideRings(mid, ringsB))) return { ...route, clipToWater: false }

  const authored = len / 2
  const at = (dir: 1 | -1, s: number): [number, number] => [mid[0] + ux * dir * s, mid[1] + uy * dir * s]
  // Walk out from the midpoint to the last distance where `ok` still holds;
  // the authored end when it holds all the way.
  const reach = (ok: (s: number) => boolean): number | null => {
    const limit = authored + Math.max(len, cap * 8)
    const step = Math.max(len / 400, cap / 6, 0.05)
    if (!ok(0)) return null
    let lo = 0, hi = -1
    for (let s = step; s <= limit; s += step) {
      if (!ok(s)) { hi = s; break }
      lo = s
    }
    if (hi < 0) return null
    for (let i = 0; i < 8; i++) {
      const m = (lo + hi) / 2
      if (ok(m)) lo = m
      else hi = m
    }
    return lo
  }
  const lineEnd = (dir: 1 | -1, rings: Array<Array<[number, number]>> | undefined): number => {
    if (!rings || rings.length === 0) return authored
    const coast = reach((s) => !insideRings(at(dir, s), rings))
    return coast === null ? authored : coast + cap
  }
  const arrowTip = (dir: 1 | -1, rings: Array<Array<[number, number]>> | undefined): number => {
    if (!rings || rings.length === 0) return authored
    const gap = edge + arrowStroke / 2, half = arrowSize * 0.6
    const tip = reach((s) => {
      const [px, py] = at(dir, s)
      const bx = px - ux * dir * arrowSize, by = py - uy * dir * arrowSize
      const probes: Array<[number, number]> = [[px, py], [bx - uy * half, by + ux * half], [bx + uy * half, by - ux * half]]
      return probes.every((p) => !insideRings(p, rings) && distanceToRings(p, rings) >= gap)
    })
    return tip ?? authored
  }

  const reachFrom = lineEnd(-1, ringsA)
  const reachTo = route.oneWay && arrowSize > 0 ? arrowTip(1, ringsB) : lineEnd(1, ringsB)
  return {
    ...route,
    from: { x: mid[0] - ux * reachFrom, y: mid[1] - uy * reachFrom },
    to: { x: mid[0] + ux * reachTo, y: mid[1] + uy * reachTo },
    clipToWater: true,
  }
}

/** The point `back` units before a route's `to` end, along the route. */
export function routeLineEnd(route: Pick<SeaRoute, 'from' | 'to'>, back: number): { x: number; y: number } {
  const dx = route.to.x - route.from.x, dy = route.to.y - route.from.y
  const len = Math.hypot(dx, dy) || 1
  return { x: route.to.x - (dx / len) * back, y: route.to.y - (dy / len) * back }
}
